/** @jsxImportSource @emotion/react */

import type { Session, SetAuthStateInput } from "./types";
import { isWeb } from "../../platform";

/**
 * Safari tab localStorage !== iOS standalone localStorage.
 * Cookies DO cross-context (same origin), so we use a short-lived cookie
 * to "handoff" auth from browser -> standalone.
 */
export const AUTH_HANDOFF_COOKIE = "mailander_auth_handoff_v1";

export type AuthHandoffPayload = {
  access_token: string;
  refresh_token: string;
  expires_at?: number; // ms (we store ms)
  expires_in?: number;
  created_at_ms: number;
  v: 1;
};

const encode = (s: string) => encodeURIComponent(s);
const decode = (s: string) => decodeURIComponent(s);

const shouldUseSecureCookie = () => {
  if (typeof window === "undefined") return false;
  return window.location.protocol === "https:";
};

export const setCookie = (
  name: string,
  value: string,
  maxAgeSeconds: number,
) => {
  if (typeof document === "undefined") return;

  const secure = shouldUseSecureCookie() ? "; Secure" : "";
  document.cookie = `${encode(name)}=${
    encode(value)
  }; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax${secure}`;
};

export const getCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;

  const encodedName = encode(name) + "=";
  const parts = document.cookie.split("; ");
  for (const part of parts) {
    if (part.startsWith(encodedName)) {
      return decode(part.substring(encodedName.length));
    }
  }
  return null;
};

export const deleteCookie = (name: string) => {
  if (typeof document === "undefined") return;

  const secure = shouldUseSecureCookie() ? "; Secure" : "";
  document.cookie = `${
    encode(name)
  }=; Max-Age=0; Path=/; SameSite=Lax${secure}`;
};

const normalizeExpiresAtMs = (
  expires_at?: number,
  expires_in?: number,
): number | undefined => {
  if (typeof expires_at === "number" && Number.isFinite(expires_at)) {
    // If it's seconds epoch, convert to ms. If already ms, keep.
    const isSeconds = expires_at < 10_000_000_000; // ~year 2286 in seconds
    return isSeconds ? expires_at * 1000 : expires_at;
  }

  if (typeof expires_in === "number" && Number.isFinite(expires_in)) {
    return Date.now() + expires_in * 1000;
  }

  return undefined;
};

/**
 * Write a short-lived cookie that both Safari tab and standalone app can read.
 * Keep it short (e.g. 30 minutes) — just for handoff.
 */
export const writeAuthHandoffCookieFromSession = (session: Session) => {
  if (!isWeb()) return; // No cookie handoff on native
  const expiresAtMs = normalizeExpiresAtMs(
    (session as any)?.expires_at,
    session.expires_in,
  );

  // ✅ exactOptionalPropertyTypes-safe: omit optional keys when undefined
  const payload: AuthHandoffPayload = {
    v: 1,
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    created_at_ms: Date.now(),
    ...(typeof session.expires_in === "number"
      ? { expires_in: session.expires_in }
      : {}),
    ...(typeof expiresAtMs === "number" ? { expires_at: expiresAtMs } : {}),
  };

  setCookie(AUTH_HANDOFF_COOKIE, JSON.stringify(payload), 30 * 60);
};

export const readAuthHandoffCookie = (): AuthHandoffPayload | null => {
  if (!isWeb()) return null;
  try {
    const raw = getCookie(AUTH_HANDOFF_COOKIE);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as AuthHandoffPayload;
    if (!parsed || parsed.v !== 1) return null;
    if (!parsed.access_token || !parsed.refresh_token) return null;

    return parsed;
  } catch {
    return null;
  }
};

export const clearAuthHandoffCookie = () => deleteCookie(AUTH_HANDOFF_COOKIE);

/**
 * Convert the cookie payload into your existing SetAuthStateInput.
 * We store expires_at in ms; SetAuthStateInput expects seconds.
 */
export const handoffPayloadToSetAuthStateInput = (
  p: AuthHandoffPayload,
): SetAuthStateInput => {
  const expires_at_ms = typeof p.expires_at === "number"
    ? p.expires_at
    : undefined;
  const expires_at_sec = typeof expires_at_ms === "number"
    ? Math.floor(expires_at_ms / 1000)
    : undefined;

  return {
    access_token: p.access_token,
    refresh_token: p.refresh_token,
    ...(typeof p.expires_in === "number" ? { expires_in: p.expires_in } : {}),
    ...(typeof expires_at_sec === "number"
      ? { expires_at: expires_at_sec }
      : {}),
  };
};
